import type { Document } from "../document/Document";
import type { Layer, RasterLayer } from "../document/Layer";
import { PixelBuffer } from "../pixel/PixelBuffer";
import { compositeLayer } from "../pixel/blend";
import type { Command } from "./Command";

// Flatten the document to a single "Background" raster layer:
// - Composite ALL visible raster layers (bottom-first, recursing through
//   visible groups) into a new raster at document size.
// - Drop every other layer, including hidden ones.
//
// Invert restores the previous layer list verbatim.
//
// Same simplifications as MergeVisible: group-level blend/opacity not yet
// modeled — children composite as if the group is pass-through opacity 1.

const FLATTENED_ID = "background";

export class FlattenCommand implements Command {
  readonly kind = "flatten";
  private previous: readonly Layer[] | null = null;

  apply(doc: Document): Document {
    this.previous = doc.layers;
    const visible = collectVisibleRasters(doc.layers);
    const buffer = new PixelBuffer(doc.width, doc.height);
    for (const r of visible) {
      const projected = projectOntoCanvas(buffer.width, buffer.height, r);
      compositeLayer(buffer.data, projected, r.blendMode, r.opacity);
    }
    buffer.markDirty();

    const background: RasterLayer = {
      kind: "raster",
      id: uniqueId(doc.layers, FLATTENED_ID),
      name: "Background",
      visible: true,
      opacity: 1,
      fillOpacity: 1,
      blendMode: "normal",
      x: 0,
      y: 0,
      lockTransparency: false,
      lockImage: false,
      lockPosition: false,
      lockAll: false,
      clippedToBelow: false,
      pixels: buffer,
    };

    return { ...doc, layers: [background], activeLayerId: background.id };
  }

  invert(before: Document, _after: Document): Command {
    const prev = this.previous ?? before.layers;
    return new RestoreFlattenCommand(prev, before.activeLayerId);
  }
}

class RestoreFlattenCommand implements Command {
  readonly kind = "flatten/restore";
  constructor(
    private readonly layers: readonly Layer[],
    private readonly activeLayerId: string | null,
  ) {}

  apply(doc: Document): Document {
    return { ...doc, layers: this.layers, activeLayerId: this.activeLayerId };
  }

  invert(_before: Document, _after: Document): Command {
    return new FlattenCommand();
  }
}

// ---------------------------------------------------------------------------
// Helpers (duplicated from MergeVisibleCommand to keep file boundaries clean)
// ---------------------------------------------------------------------------

function collectVisibleRasters(layers: readonly Layer[]): RasterLayer[] {
  const out: RasterLayer[] = [];
  walk(layers, true, out);
  return out;
}

function walk(layers: readonly Layer[], parentVisible: boolean, acc: RasterLayer[]): void {
  for (const l of layers) {
    const v = parentVisible && l.visible;
    if (!v) continue;
    if (l.kind === "raster") acc.push(l);
    else if (l.kind === "group") walk(l.children, v, acc);
  }
}

function projectOntoCanvas(
  canvasW: number,
  canvasH: number,
  layer: RasterLayer,
): Uint8ClampedArray {
  const out = new Uint8ClampedArray(canvasW * canvasH * 4);
  const lw = layer.pixels.width;
  const lh = layer.pixels.height;
  const x0 = Math.max(0, layer.x);
  const y0 = Math.max(0, layer.y);
  const x1 = Math.min(canvasW, layer.x + lw);
  const y1 = Math.min(canvasH, layer.y + lh);
  for (let y = y0; y < y1; y++) {
    const srcRow = y - layer.y;
    for (let x = x0; x < x1; x++) {
      const srcCol = x - layer.x;
      const srcOff = (srcRow * lw + srcCol) * 4;
      const dstOff = (y * canvasW + x) * 4;
      out[dstOff] = layer.pixels.data[srcOff]!;
      out[dstOff + 1] = layer.pixels.data[srcOff + 1]!;
      out[dstOff + 2] = layer.pixels.data[srcOff + 2]!;
      out[dstOff + 3] = layer.pixels.data[srcOff + 3]!;
    }
  }
  return out;
}

function uniqueId(layers: readonly Layer[], base: string): string {
  const taken = new Set<string>();
  collectIds(layers, taken);
  if (!taken.has(base)) return base;
  let i = 1;
  while (taken.has(`${base}-${i}`)) i++;
  return `${base}-${i}`;
}

function collectIds(layers: readonly Layer[], out: Set<string>): void {
  for (const l of layers) {
    out.add(l.id);
    if (l.kind === "group") collectIds(l.children, out);
  }
}

